import React from "react";
import { LegalPageLayout, LegalSection } from "@/pages/LegalPage";

/**
 * /privacy-policy — Kebijakan Privasi (Bahasa Indonesia).
 * Content is intentionally source-controlled (not admin-CMS) so the legal
 * language stays stable and reviewable via git history.
 */
export default function PrivacyPolicy() {
  return (
    <LegalPageLayout
      title="Kebijakan Privasi"
      subtitle="Kami percaya orang tua berhak tahu persis data apa yang kami simpan dan bagaimana data itu dipakai. Halaman ini menjelaskannya dalam bahasa yang jelas — tanpa jargon hukum yang membingungkan."
      lastUpdated="13 Agustus 2026"
      testid="privacy-policy-page"
      seoDesc="Kebijakan Privasi The School Reports — data yang kami kumpulkan, cara kami menggunakannya, dan hak Anda sebagai pengguna."
    >
      <LegalSection heading="1. Data yang Kami Kumpulkan" testid="privacy-sec-data">
        <p>Saat Anda membuat akun dan berinteraksi di The School Reports (TSR), kami menyimpan data berikut:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Nama & alamat email</strong> — dari pendaftaran akun manual atau login Google.</li>
          <li><strong>Review sekolah</strong> — teks penilaian, rating per-kategori, dan metadata (jenjang anak, tahun lulus, dsb.) yang Anda isikan pada form review.</li>
          <li><strong>Foto</strong> — gambar yang Anda unggah untuk review sekolah atau profil.</li>
          <li><strong>Aktivitas di forum</strong> — thread, balasan, dan bookmark yang Anda buat.</li>
          <li><strong>Data teknis dasar</strong> — alamat IP, jenis perangkat, dan waktu login, terbatas untuk mencegah spam/penyalahgunaan.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="2. Bagaimana Data Digunakan" testid="privacy-sec-usage">
        <p>Data Anda kami gunakan strictly untuk menjalankan platform:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Menampilkan profil sekolah beserta ringkasan review yang Anda kirimkan.</li>
          <li>Menampilkan nama & foto profil Anda pada review dan thread forum.</li>
          <li>Mengirim notifikasi email terkait aktivitas akun (mis. balasan pada thread Anda), <em>hanya</em> jika Anda mengaktifkan preferensi tersebut.</li>
          <li>Mencegah penyalahgunaan platform (deteksi spam, akun ganda, review palsu).</li>
        </ul>
        <p>Kami <strong>tidak</strong> menjual, menyewakan, atau memberikan data Anda kepada pihak ketiga untuk kepentingan pemasaran.</p>
      </LegalSection>

      <LegalSection heading="3. Berbagi Data dengan Pihak Ketiga" testid="privacy-sec-third-party">
        <p>TSR hanya berbagi data dalam kondisi terbatas berikut:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Penyedia layanan teknis</strong> — MongoDB (basis data), Amazon Web Services (hosting foto, diskusi), Google (jika Anda memilih login via Google Auth). Setiap penyedia terikat kontrak yang melarang penggunaan data Anda di luar keperluan operasional TSR.</li>
          <li><strong>Perintah hukum</strong> — kami hanya menyerahkan data kepada penegak hukum bila menerima permintaan resmi yang sah menurut hukum Republik Indonesia.</li>
        </ul>
        <p>Review, komentar forum, dan foto yang Anda posting bersifat publik — artinya siapa saja yang membuka halaman terkait dapat membacanya. Pertimbangkan hal ini sebelum menulis.</p>
      </LegalSection>

      <LegalSection heading="4. Hak Anda atas Data Pribadi" testid="privacy-sec-rights">
        <p>Anda berhak untuk:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Meminta salinan</strong> semua data yang kami simpan tentang Anda.</li>
          <li><strong>Mengoreksi</strong> data yang tidak akurat (mis. mengubah nama tampilan) langsung dari halaman Profil.</li>
          <li><strong>Menghapus</strong> akun Anda beserta seluruh data pribadi yang melekat padanya. Review & thread anonimisasi (nama diganti "Pengguna dihapus") akan tetap tampil demi menjaga integritas diskusi publik, kecuali Anda meminta penghapusan spesifik.</li>
          <li><strong>Menarik persetujuan</strong> pengiriman email marketing kapan saja lewat link "berhenti berlangganan" di setiap email.</li>
        </ul>
        <p>Untuk mengeksekusi hak-hak tersebut, kirim email ke alamat di bawah dan kami akan merespons dalam <strong>maksimal 14 hari kerja</strong>.</p>
      </LegalSection>

      <LegalSection heading="5. Keamanan Data" testid="privacy-sec-security">
        <p>Data disimpan di server yang menerapkan enkripsi in-transit (HTTPS/TLS) dan at-rest. Password akun manual disimpan dalam bentuk hash bcrypt — tim TSR sendiri tidak bisa melihat password Anda. Meski begitu, tidak ada sistem yang 100% kebal — segera hubungi kami bila Anda menduga akun disalahgunakan.</p>
      </LegalSection>

      <LegalSection heading="6. Perubahan Kebijakan" testid="privacy-sec-changes">
        <p>Kami dapat memperbarui kebijakan ini dari waktu ke waktu. Perubahan besar akan diumumkan lewat email ke pengguna terdaftar dan pengumuman di halaman muka. Tanggal "Terakhir diperbarui" di atas selalu mencerminkan versi yang berlaku.</p>
      </LegalSection>

      <LegalSection heading="7. Kontak Privasi" testid="privacy-sec-contact">
        <p>Pertanyaan, keluhan, atau permintaan terkait data pribadi Anda dapat dikirim ke:</p>
        <p>
          <a href="mailto:hello@theschoolreports.id" className="text-[#D2673E] hover:underline">
            hello@theschoolreports.id
          </a>
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}
